const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

export const UI_TEST_CHAR_DELAY_MS = 45
export const UI_TEST_STEP_DELAY_MS = 350

function setNativeFieldValue(element: HTMLInputElement | HTMLTextAreaElement, value: string) {
  const proto =
    element instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set
  setter?.call(element, value)
  element.dispatchEvent(new Event('input', { bubbles: true }))
  element.dispatchEvent(new Event('change', { bubbles: true }))
}

export async function waitFor(
  predicate: () => boolean,
  timeoutMs = 10_000,
  intervalMs = 100,
): Promise<void> {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    if (predicate()) return
    await sleep(intervalMs)
  }
  throw new Error('Hết thời gian chờ thao tác UI')
}

export async function waitForAsync(
  predicate: () => Promise<boolean>,
  timeoutMs = 15_000,
  intervalMs = 250,
): Promise<void> {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    if (await predicate()) return
    await sleep(intervalMs)
  }
  throw new Error('Hết thời gian chờ thao tác UI')
}

/** Gán giá trị ô nhập — đồng bộ state React (dùng cho form sửa). */
export async function fillFieldValue(
  element: HTMLInputElement | HTMLTextAreaElement,
  text: string,
): Promise<void> {
  element.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  element.focus()
  element.click()
  setNativeFieldValue(element, text)
  element.dispatchEvent(new Event('blur', { bubbles: true }))
  await sleep(UI_TEST_STEP_DELAY_MS)
}

export function findInRoot(root: HTMLElement, selector: string): HTMLElement | null {
  return root.querySelector(selector) as HTMLElement | null
}

export function findButtonByText(
  root: HTMLElement,
  text: string,
  options?: { contained?: boolean },
): HTMLButtonElement | null {
  const selector = options?.contained ? 'button.MuiButton-contained' : 'button'
  const buttons = root.querySelectorAll(selector)
  for (const node of Array.from(buttons)) {
    const btn = node as HTMLButtonElement
    const label = btn.textContent?.replace(/\s+/g, ' ').trim() ?? ''
    if (label.includes(text)) return btn
  }
  return null
}

export function findFieldByName(
  root: HTMLElement,
  name: string,
): HTMLInputElement | HTMLTextAreaElement | null {
  return root.querySelector(`input[name="${name}"], textarea[name="${name}"]`) as
    | HTMLInputElement
    | HTMLTextAreaElement
    | null
}

export async function clickButton(
  root: HTMLElement,
  text: string,
  options?: { contained?: boolean },
): Promise<void> {
  const btn = findButtonByText(root, text, options)
  if (!btn) throw new Error(`Không tìm thấy nút "${text}"`)
  if (btn.disabled) throw new Error(`Nút "${text}" đang bị khóa`)
  btn.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  btn.focus()
  btn.click()
  await sleep(UI_TEST_STEP_DELAY_MS)
}

export async function clickElement(element: HTMLElement): Promise<void> {
  element.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  element.focus()
  element.click()
  await sleep(UI_TEST_STEP_DELAY_MS)
}

/** Gõ từng ký tự — giống người nhập liệu. */
export async function typeSlowly(
  element: HTMLInputElement | HTMLTextAreaElement,
  text: string,
  charDelayMs = UI_TEST_CHAR_DELAY_MS,
  options?: { blur?: boolean },
): Promise<void> {
  element.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  element.focus()
  element.click()
  setNativeFieldValue(element, '')

  for (let i = 0; i < text.length; i += 1) {
    setNativeFieldValue(element, text.slice(0, i + 1))
    await sleep(charDelayMs)
  }

  if (options?.blur !== false) {
    element.dispatchEvent(new Event('blur', { bubbles: true }))
  }
  await sleep(UI_TEST_STEP_DELAY_MS)
}

export async function waitForTopDialog(timeoutMs = 10_000): Promise<HTMLElement> {
  let dialog: HTMLElement | null = null
  await waitFor(
    () => {
      const dialogs = document.querySelectorAll('[role="dialog"]')
      if (dialogs.length === 0) return false
      dialog = dialogs[dialogs.length - 1] as HTMLElement
      return true
    },
    timeoutMs,
    80,
  )
  if (!dialog) throw new Error('Không mở được hộp thoại')
  await sleep(UI_TEST_STEP_DELAY_MS)
  return dialog
}

export async function clickDialogSaveButton(dialog: HTMLElement): Promise<void> {
  const actions = dialog.querySelector('.MuiDialogActions-root')
  const scope = actions ?? dialog
  const contained = scope.querySelectorAll('button.MuiButton-containedPrimary, button.MuiButton-contained')
  const saveBtn = contained[contained.length - 1] as HTMLButtonElement | undefined
  if (!saveBtn) throw new Error('Không tìm thấy nút Lưu trong modal')
  if (saveBtn.disabled) throw new Error('Nút Lưu đang bị khóa')
  await clickElement(saveBtn)
}

export async function waitForDialogClosed(dialog: HTMLElement, timeoutMs = 20_000): Promise<void> {
  await waitFor(() => !document.body.contains(dialog), timeoutMs, 150)
  await sleep(UI_TEST_STEP_DELAY_MS)
}

export async function clickDialogConfirmButton(dialog?: HTMLElement): Promise<void> {
  const scope = dialog ?? (await waitForTopDialog())
  const actions = scope.querySelector('.MuiDialogActions-root') ?? scope
  const btn =
    (actions.querySelector(
      'button.MuiButton-containedError, button.MuiButton-contained.MuiButton-colorError',
    ) as HTMLButtonElement | null) ??
    (Array.from(actions.querySelectorAll('button.MuiButton-contained')).pop() as
      | HTMLButtonElement
      | undefined)
  if (!btn) throw new Error('Không tìm thấy nút xác nhận trong hộp thoại')
  if (btn.disabled) throw new Error('Nút xác nhận đang bị khóa')
  await clickElement(btn)
}

export function findRowByMa(root: HTMLElement, ma: string): HTMLElement | null {
  return root.querySelector(`[data-ui-test-row-ma="${CSS.escape(ma)}"]`) as HTMLElement | null
}

/** Tìm dòng bảng chứa chuỗi (khi chưa có data-ui-test-row-ma). */
export function findRowContaining(root: HTMLElement, text: string): HTMLElement | null {
  const needle = text.trim()
  if (!needle) return null
  const rows = root.querySelectorAll('tbody tr')
  for (const row of Array.from(rows)) {
    if (row.textContent?.includes(needle)) return row as HTMLElement
  }
  return null
}

export async function pressEnter(element: HTMLElement): Promise<void> {
  element.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true, cancelable: true }),
  )
  await sleep(UI_TEST_STEP_DELAY_MS)
}
